import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, Dialog, Field, Input, MultiSelect, Segmented, Switch, Textarea, toast, useConfirm } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AssemblyVersionClient, MARKETS, refusalMessage, VersionProfileClient, type VersionProfileDTO } from "../../api/manufacturing";
import { NexpimClientClient, clientLabel } from "../../api/pricing";
import { NexpimView } from "../../views";

const KEY = ["manufacturing", "version-profile"];

/**
 * When an assembly version is used (design §14): rules defined once and set on
 * the versions of many products, on the product's Assembly tab. A rule names
 * markets (the client's currency) and clients; a demand matches when it is in
 * one of its markets and, if clients are named, from one of them. Strict: only
 * its versions serve it. Preferred: the default when they have none in stock.
 * Switching a rule off sends its demand back to the defaults.
 */
export function VersionRulesPage() {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const allowed = hasView(NexpimView.MANUFACTURING_PARTS);
  const client = useMemo(() => new VersionProfileClient(http), [http]);
  const list = useQuery({ queryKey: KEY, queryFn: () => client.findAll(), enabled: allowed });
  const clients = useQuery({ queryKey: ["client", "active"], queryFn: () => new NexpimClientClient(http).active(), enabled: allowed });
  const clientName = useMemo(() => new Map((clients.data ?? []).map((one) => [String(one.cli_id), clientLabel(one)])), [clients.data]);
  const [editing, setEditing] = useState<VersionProfileDTO | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const title = t("navigation.item.config-version-rule", "Version rules");
  const rows = list.data ?? [];
  const selected = rows.find((row) => row.id === selectedId) ?? null;

  const remove = useMutation({
    mutationFn: (profile: VersionProfileDTO) => client.remove(profile.id),
    onSuccess: async () => {
      setSelectedId(null);
      await queryClient.invalidateQueries({ queryKey: ["manufacturing"] });
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const describe = (profile: VersionProfileDTO) => {
    const markets = profile.conditions.filter((one) => one.dimension === "MARKET").map((one) => one.value);
    const named = profile.conditions.filter((one) => one.dimension === "CLIENT").map((one) => clientName.get(one.value) ?? `#${one.value}`);
    const parts = [
      markets.length > 0 ? t("rule.describe.markets", "market {{list}}", { list: markets.join(t("rule.describe.or", " or ")) }) : null,
      named.length > 0 ? t("rule.describe.clients", "client {{list}}", { list: named.join(t("rule.describe.or", " or ")) }) : null,
    ].filter(Boolean);
    return parts.length === 0 ? t("rule.describe.nothing", "Matches nothing: no condition") : parts.join(t("rule.describe.and", " and "));
  };

  const columns = useMemo(() => {
    const c = defineColumns<VersionProfileDTO>();
    return [
      c.text("name", (row) => row.name, { header: t("rule.name", "Rule"), priority: 1 }),
      c.text("conditions", (row) => describe(row), { header: t("rule.when", "Used for"), priority: 1 }),
      c.text("substitution", (row) => (row.substitution === "STRICT" ? t("rule.strict", "Strict") : t("rule.preferred", "Preferred")), {
        header: t("rule.mode", "Mode"),
        filter: "select",
        priority: 2,
      }),
      c.custom("is_active", (row) => (row.is_active ? "1" : "0"), (row) =>
        row.is_active ? <Badge size="sm" variant="success">{t("rule.on", "On")}</Badge> : <Badge size="sm" variant="neutral">{t("rule.off", "Off")}</Badge>,
        { header: t("rule.state", "State"), priority: 2 }),
      c.number("version_count", (row) => row.version_count ?? 0, { header: t("rule.versions", "Versions"), priority: 2 }),
    ];
    // describe reads the client names, loaded after the rules.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, clientName]);

  if (!allowed) {
    return (
      <>
        <PageHeader title={title} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">{t("error.access-denied.detail", "Your account does not have permission to view this page.")}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={title}
        subtitle={t(
          "rule.subtitle",
          "When a product's assembly version is used: by market (the client's currency) or by client. Set a rule on a version from the product's Assembly tab; the default version serves whatever no rule takes.",
        )}
        actions={
          canWrite ? (
            <Button variant="primary" icon={<Plus className="size-4" />}
              onClick={() => setEditing({ id: 0, name: "", substitution: "STRICT", is_active: true, note: null, conditions: [] })}>
              {t("rule.new", "New rule")}
            </Button>
          ) : null
        }
      />
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <div className="flex min-h-64 min-w-0 flex-1 flex-col">
          <DataTable<VersionProfileDTO>
            data={rows}
            columns={columns}
            loading={list.isLoading}
            getRowId={(row) => String(row.id)}
            onRowClick={(row) => setSelectedId(row.id)}
            rowClassName={(row) => (row.id === selected?.id ? "bg-accent-soft" : undefined)}
            stateKey="config.version-rule"
            rowActions={(row) =>
              canWrite ? (
                <Button size="sm" variant="ghost" onClick={() => setEditing(row)}>
                  {t("button.edit", "Edit")}
                </Button>
              ) : null
            }
            emptyMessage={t("rule.empty", "No rule yet. The go-live script makes \"US tariff boxing\" for the US versions.")}
          />
        </div>
        {selected && (
          <aside className="flex w-full shrink-0 flex-col border-t border-line lg:w-96 lg:border-l lg:border-t-0">
            <UsedBy profile={selected} />
            {canWrite && (
              <div className="border-t border-line px-4 py-3">
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    void (async () => {
                      const ok = await confirm({
                        title: t("rule.delete.title", "Delete {{name}}?", { name: selected.name }),
                        message: t("rule.delete.message", "{{n}} versions lose it and stop being chosen: their demand goes to their products' defaults.", { n: selected.version_count ?? 0 }),
                        confirmLabel: t("rule.delete.confirm", "Delete"),
                        variant: "danger",
                      });
                      if (ok) remove.mutate(selected);
                    })();
                  }}
                >
                  {t("rule.delete", "Delete the rule")}
                </Button>
              </div>
            )}
          </aside>
        )}
      </div>
      {editing && <RuleDialog profile={editing} clientOptions={(clients.data ?? []).map((one) => ({ value: String(one.cli_id), label: clientLabel(one) }))} onClose={() => setEditing(null)} />}
    </>
  );
}

/** The versions a rule is set on, each a link to its product's Assembly tab. */
function UsedBy({ profile }: { profile: VersionProfileDTO }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const versions = useQuery({
    queryKey: ["manufacturing", "assembly-version", "profile", profile.id],
    queryFn: async () => (await new AssemblyVersionClient(http).find({ size: 500, filters: { version_profile_id: [String(profile.id)] } })).content,
  });
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-auto px-4 py-3">
      <h3 className="mb-2 text-[13px] font-semibold">{t("rule.used-by", "Set on {{n}} versions", { n: versions.data?.length ?? 0 })}</h3>
      <ul className="flex flex-col gap-1 text-[13px]">
        {(versions.data ?? []).map((version) => (
          <li key={version.id} className="flex items-center gap-2">
            <Link to="/products/$productId" params={{ productId: String(version.product_id) }} search={{ tab: "assembly" }}
              className="text-accent underline-offset-2 hover:underline">
              {version.product_sku}
            </Link>
            <span className="font-semibold">{version.code}</span>
            <span className="font-mono text-[12px] text-ink-muted">{version.output_part_number}</span>
            {version.part_status !== "ACTIVE" && <Badge size="sm" variant="neutral">{t("assembly.version.off", "Off")}</Badge>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function RuleDialog({ profile, clientOptions, onClose }: { profile: VersionProfileDTO; clientOptions: Array<{ value: string; label: string }>; onClose: () => void }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const [name, setName] = useState(profile.name);
  const [substitution, setSubstitution] = useState(profile.substitution);
  const [active, setActive] = useState(profile.is_active);
  const [note, setNote] = useState(profile.note ?? "");
  const [markets, setMarkets] = useState(profile.conditions.filter((one) => one.dimension === "MARKET").map((one) => one.value));
  const [clients, setClients] = useState(profile.conditions.filter((one) => one.dimension === "CLIENT").map((one) => one.value));
  const valid = name.trim() !== "" && (markets.length > 0 || clients.length > 0);
  const save = useMutation({
    mutationFn: () => {
      const body: VersionProfileDTO = {
        ...profile,
        name: name.trim(),
        substitution,
        is_active: active,
        note: note.trim() || null,
        conditions: [
          ...markets.map((value) => ({ dimension: "MARKET" as const, value })),
          ...clients.map((value) => ({ dimension: "CLIENT" as const, value })),
        ],
      };
      const client = new VersionProfileClient(http);
      return profile.id === 0 ? client.create(body).then(() => undefined) : client.update(body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["manufacturing"] });
      toast.success(t("notification.saved", "Saved"));
      onClose();
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  return (
    <Dialog
      open
      size="lg"
      onClose={onClose}
      title={profile.id === 0 ? t("rule.new", "New rule") : t("rule.edit", "Edit {{name}}", { name: profile.name })}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>
            {t("button.save", "Save")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label={t("rule.name", "Rule")} htmlFor="rule-name" required>
          <Input id="rule-name" value={name} autoFocus placeholder={t("rule.name.placeholder", "US tariff boxing")} onChange={(event) => setName(event.target.value)} />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("rule.markets", "Markets")} htmlFor="rule-markets" hint={t("rule.markets.hint", "The client's currency: USD is US, CAD is CA.")}>
            <MultiSelect id="rule-markets" value={markets} options={MARKETS.map((market) => ({ value: market, label: market }))} onChange={setMarkets}
              placeholder={t("rule.any", "Any")} />
          </Field>
          <Field label={t("rule.clients", "Clients")} htmlFor="rule-clients" hint={t("rule.clients.hint", "Empty: any client of those markets.")}>
            <MultiSelect id="rule-clients" value={clients} options={clientOptions} onChange={setClients} placeholder={t("rule.any", "Any")} />
          </Field>
        </div>
        <p className="text-[12px] text-ink-muted">
          {markets.length === 0 && clients.length === 0
            ? t("rule.need-one", "Name a market or a client: a rule with no condition matches nothing.")
            : t("rule.reads", "Used for {{what}}.", {
                what: [
                  markets.length > 0 ? t("rule.describe.markets", "market {{list}}", { list: markets.join(t("rule.describe.or", " or ")) }) : null,
                  clients.length > 0 ? t("rule.describe.clients", "client {{list}}", {
                    list: clients.map((value) => clientOptions.find((one) => one.value === value)?.label ?? value).join(t("rule.describe.or", " or ")),
                  }) : null,
                ].filter(Boolean).join(t("rule.describe.and", " and ")),
              })}
        </p>
        <Field label={t("rule.mode", "Mode")} htmlFor="rule-mode">
          <span className="flex flex-col gap-1">
            <Segmented
              value={substitution}
              onChange={setSubstitution}
              options={[
                { value: "STRICT", label: t("rule.strict", "Strict") },
                { value: "PREFERRED", label: t("rule.preferred", "Preferred") },
              ]}
            />
            <span className="text-[12px] text-ink-muted">
              {substitution === "STRICT"
                ? t("rule.strict.hint", "Only this version serves what the rule matches: out of stock means unavailable there.")
                : t("rule.preferred.hint", "This version first; the default when it has none.")}
            </span>
          </span>
        </Field>
        <Switch checked={active} onChange={setActive} label={t("rule.active", "On: off sends its demand back to the defaults")} />
        <Field label={t("form.field.note", "Note")} htmlFor="rule-note">
          <Textarea id="rule-note" rows={2} value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
